import { AxiosResponse } from 'axios';
import { parseError } from '@/features/common/helpers/errorParser';
import { useAxios } from '@/features/common/helpers/axios';
import { GlobalResponseType } from '@/features/common/types';
import i18n from '@/i18n';
import { CarrierAccountType } from '../types';
import {
  FedExValidateAddressRequest,
  FedExValidateAddressResponse,
  FedExGeneratePinRequest,
  FedExGeneratePinResponse,
  FedExValidatePinRequest,
  FedExValidateInvoiceRequest,
} from '../types/fedexRegistration';

export const { axiosService } = useAxios();

/**
 * Strip FedEx error code prefixes like "ACCOUNT.ADDRESS.MISMATCH: " from messages,
 * leaving just the human-readable part (e.g. "Account Address mismatch.").
 */
const cleanErrorMessage = (message: string): string => {
  if (!message) return message;
  const match = message.match(/^[A-Z0-9]+(?:\.[A-Z0-9]+)+:\s*(.+)$/);
  return match ? match[1] : message;
};

const filterExceptionMessage = (err: GlobalResponseType): GlobalResponseType => {
  if (err.errorType) {
    const messageKey = `carrierAccounts.errorMessages.${err.errorType}`;
    if (i18n.te(messageKey)) {
      return {
        ...err,
        errorMessage: i18n.t(messageKey) as string,
      };
    }
  }

  return {
    ...err,
    errorMessage: cleanErrorMessage(err.errorMessage),
  };
};

export const validateFedExAddress = async (
  data: FedExValidateAddressRequest,
): Promise<{ data?: FedExValidateAddressResponse } & GlobalResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.post(
      '/my-accounts/fedex-registration/validate-address',
      data,
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      data: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const generateFedExPin = async (
  data: FedExGeneratePinRequest,
): Promise<{ data?: FedExGeneratePinResponse } & GlobalResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.post(
      '/my-accounts/fedex-registration/generate-pin',
      data,
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      data: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

export const validateFedExPin = async (
  data: FedExValidatePinRequest,
): Promise<{ data?: CarrierAccountType[] } & GlobalResponseType> => {
  try {
    const response: AxiosResponse = await axiosService.post(
      '/my-accounts/fedex-registration/validate-pin',
      data,
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      data: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};

/**
 * FedEx requires invoiceAmount with exactly 2 decimal places (e.g. 234.00, not 234).
 * Since JSON.stringify strips trailing zeros from numbers, we use a placeholder
 * replacement to ensure the correct format in the serialized payload.
 */
export const validateFedExInvoice = async (
  data: FedExValidateInvoiceRequest,
): Promise<{ data?: CarrierAccountType[] } & GlobalResponseType> => {
  try {
    const { invoiceAmount, ...rest } = data;
    const body = JSON.stringify({ ...rest, invoiceAmount: null })
      .replace('"invoiceAmount":null', `"invoiceAmount":${invoiceAmount.toFixed(2)}`);

    const response: AxiosResponse = await axiosService.post(
      '/my-accounts/fedex-registration/validate-invoice',
      body,
      { headers: { 'Content-Type': 'application/json' } },
    );

    return {
      successful: true,
      errorMessage: '',
      redirectUrl: '',
      data: response.data,
    };
  } catch (err) {
    return filterExceptionMessage(parseError(err));
  }
};
